import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { Color } from '../../_kernel/Color'
import { I18nText } from '../../_kernel/I18nText'
import { ImageUrlSet } from '../../_kernel/ImageUrlSet'
import { Language } from '../../shared/domain/Language'
import { ListOfI18nText } from '../../_kernel/ListOfI18nText'
import { Url } from '../../_kernel/Url'
import { ALLOWED_MARKETS, Market } from '../../shared/domain/Market'
import { BannerType, type ALLOWED_BANNER_TYPE } from './BannerType'
import { type ALLOWED_BANNER_SUB_TYPE, BannerSubtype } from './BannerSubtype'
import { DomainError } from '../../_kernel/DomainError'
import { ErrorsCode } from '../../_kernel/ErrorsCode'
import { type GETBannerInformationResponseType } from '../infrastructure/HTTPMarketingRepository/schemas/banners/banners-json'

export const ALLOWED_BANNER_SUBTYPES_BY_TYPE = {
  'billboard-banner': ['brand', 'campaign', 'member'],
} as Partial<Record<(typeof ALLOWED_BANNER_TYPE)[number], Partial<typeof ALLOWED_BANNER_SUB_TYPE>>>

export const BannerValidation = z.object({
  type: z.instanceof(BannerType, { message: '[Banner] type value is required' }),
  subtype: z.instanceof(BannerSubtype).optional(),
  market: z.instanceof(Market, { message: '[Banner] market value is required' }),
  language: z.instanceof(Language, { message: '[Banner] language is required' }),
  content: z.record(
    z.enum(ALLOWED_MARKETS).or(z.literal('all')),
    z.object({
      title: z.instanceof(ListOfI18nText, { message: '[Banner] title value is required' }),
      description: z.instanceof(I18nText).optional(),
      url: z.instanceof(Url).optional(),
      ctaText: z.instanceof(I18nText).optional(),
      bgColor: z.instanceof(Color).optional(),
      titleColor: z.instanceof(Color).optional(),
      descriptionColor: z.instanceof(Color).optional(),
      image: z.instanceof(ImageUrlSet).optional(),
    })
  ),
})

export class Banner extends Model {
  static create({ type, subtype, market, language, content }: z.infer<typeof BannerValidation>) {
    BannerValidation.parse({ type, subtype, market, language, content })
    if (subtype) {
      const validSubtypes = ALLOWED_BANNER_SUBTYPES_BY_TYPE[type.value]
      if (!validSubtypes?.includes(subtype.value)) {
        throw DomainError.withCodes(ErrorsCode.INVALID_BANNER_SUBTYPE).withCauses(
          new Error(`Invalid subtype ${subtype.value} for type ${type.value}`)
        )
      }
    }
    return new Banner(type, market, language, content, subtype)
  }

  static createEmptyHeroBanner(market: Market, language: Language) {
    const type = BannerType.create({ value: 'hero-banner' })
    const content = {
      all: {
        title: ListOfI18nText.create({
          list: [
            I18nText.create({ value: 'hero.long-stays' }),
            I18nText.create({ value: 'hero.hollidays' }),
            I18nText.create({ value: 'hero.adventures' }),
            I18nText.create({ value: 'hero.getaways' }),
            I18nText.create({ value: 'hero.discoveries' }),
            I18nText.create({ value: 'hero.dates' }),
            I18nText.create({ value: 'hero.kids' }),
          ],
        }),
        image: ImageUrlSet.fromDataset({
          image: {
            srcSet: [
              'https://static.stayforlong.com/web/images/banners/hero-banner-mobile.webp',
              'https://static.stayforlong.com/web/images/banners/hero-banner-tablet.webp',
              'https://static.stayforlong.com/web/images/banners/hero-banner-desktop.webp',
            ],
            alt: { default: 'Stayforlong Hero Banner' },
          },
        }),
      },
    }

    return Banner.create({
      type,
      market,
      language,
      content,
    })
  }

  static fromDataset({
    info,
    market,
    language,
  }: {
    info: GETBannerInformationResponseType['banners'][number]
    market: Market
    language: Language
  }) {
    return Banner.#createFromApiData({
      info,
      market,
      language,
      useLegacyProcessing: true,
    })
  }

  static fromJSON(json: ReturnType<Banner['toJSON']>, market: Market, language: Language) {
    return new Banner(
      BannerType.create({ value: json.type }),
      market,
      language,
      Object.fromEntries(
        Object.entries(json.content).map(([key, value]) => [
          key,
          {
            title: ListOfI18nText.fromJSON(value.title),
            ...(value.description && { description: I18nText.create(value.description) }),
            ...(value.url && { url: Url.fromJSON(value.url) }),
            ...(value.ctaText && { ctaText: I18nText.create(value.ctaText) }),
            ...(value.bgColor && { bgColor: Color.create(value.bgColor) }),
            ...(value.titleColor && { titleColor: Color.create(value.titleColor) }),
            ...(value.descriptionColor && {
              descriptionColor: Color.create(value.descriptionColor),
            }),
            ...(value.image && { image: ImageUrlSet.fromJSON(value.image) }),
          },
        ])
      ),
      BannerSubtype.createOrUndefined(json.subtype)
    )
  }

  static fromCurrentApiDataset({
    info,
    market,
    language,
  }: {
    info: GETBannerInformationResponseType['banners'][number]
    market: Market
    language: Language
  }) {
    return Banner.#createFromApiData({
      info,
      market,
      language,
      useLegacyProcessing: false,
    })
  }

  static #createFromApiData({
    info,
    market,
    language,
    useLegacyProcessing,
  }: {
    info: GETBannerInformationResponseType['banners'][number]
    market: Market
    language: Language
    useLegacyProcessing: boolean
  }) {
    return Banner.create({
      type: BannerType.create({ value: info.type }),
      ...(info.subtype && { subtype: BannerSubtype.create({ value: info.subtype }) }),
      market,
      language,
      content: Object.fromEntries(
        Object.entries(info.content).map(([key, value]) => [
          key,
          {
            title: useLegacyProcessing
              ? ListOfI18nText.fromDataset({ list: value.title })
              : Banner.#processTitle(value.title),
            ...(value.description && {
              description: useLegacyProcessing
                ? I18nText.create({ value: value.description })
                : Banner.#processDescription(value.description),
            }),
            ...(value.url && { url: Url.fromJSON({ value: value.url }) }),
            ...(value.ctaText && {
              ctaText: useLegacyProcessing
                ? I18nText.create({ value: value.ctaText })
                : Banner.#processCtaText(value.ctaText),
            }),
            ...(value.bgColor && { bgColor: Color.create({ value: value.bgColor }) }),
            ...(value.titleColor && { titleColor: Color.create({ value: value.titleColor }) }),
            ...(value.descriptionColor && {
              descriptionColor: Color.create({ value: value.descriptionColor }),
            }),
            ...(value.image && {
              image: useLegacyProcessing
                ? ImageUrlSet.fromDataset({ image: value.image })
                : ImageUrlSet.fromCurrentApiDataset({ image: value.image }),
            }),
          },
        ])
      ),
    })
  }

  static #processTitle(title: string | string[] | Array<string | Partial<Record<string, string>>>) {
    if (Array.isArray(title)) {
      return ListOfI18nText.fromDataset({
        list: title.map(t => {
          if (typeof t === 'string') {
            return { default: t }
          }
          return t
        }),
      })
    }
    return ListOfI18nText.fromDataset({ list: [{ default: String(title) }] })
  }

  static #processDescription(description: string | string[] | Partial<Record<string, string>>) {
    if (typeof description === 'string') {
      return I18nText.create({ value: description })
    }
    if (Array.isArray(description)) {
      return I18nText.create({ value: description.join(' ') })
    }
    return I18nText.create({ value: description.default || '' })
  }

  static #processCtaText(ctaText: string | string[] | Partial<Record<string, string>>) {
    if (typeof ctaText === 'string') {
      return I18nText.create({ value: ctaText })
    }
    if (Array.isArray(ctaText)) {
      return I18nText.create({ value: ctaText.join(' ') })
    }
    return I18nText.create({ value: ctaText.default || '' })
  }

  constructor(
    public readonly _type: BannerType,
    private readonly _market: Market,
    private readonly _language: Language,
    public readonly _content: z.infer<typeof BannerValidation>['content'],
    public readonly _subtype?: BannerSubtype
  ) {
    super()
  }

  get type() { return this._type } // prettier-ignore
  get subtype() { return this._subtype } // prettier-ignore
  get content() { return this._content } // prettier-ignore
  get market() { return this._market } // prettier-ignore
  get language() { return this._language } // prettier-ignore

  toJSON() {
    return {
      type: this.type.value,
      ...(this.subtype && { subtype: this.subtype.value }),
      market: this.market.value,
      language: this.language.value,
      content: Object.fromEntries(
        Object.entries(this.content).map(([key, value]) => [
          key,
          {
            title: value.title.toJSON(),
            ...(value.description && { description: value.description.toJSON() }),
            ...(value.url && { url: value.url.toJSON() }),
            ...(value.ctaText && { ctaText: value.ctaText.toJSON() }),
            ...(value.bgColor && { bgColor: value.bgColor.toJSON() }),
            ...(value.titleColor && { titleColor: value.titleColor.toJSON() }),
            ...(value.descriptionColor && { descriptionColor: value.descriptionColor.toJSON() }),
            ...(value.image && { image: value.image.toJSON() }),
          },
        ])
      ),
    }
  }

  #getBanner() {
    let banner = this.content.all

    if (this.market.isFilled()) {
      banner = this.content[this.market.value]
    }

    if (!banner) {
      banner = this.content.all
    }

    if (!banner) {
      return undefined
    }

    return banner
  }

  #isValidAnnouncementBanner() {
    return Object.values(this.content).reduce((isValid, value) => {
      return Boolean(isValid && value.title && value.title.list.length > 0)
    }, true)
  }

  #isValidBillboardBanner() {
    return Object.values(this.content).reduce((isValid, value) => {
      return Boolean(isValid && value.title && value.description)
    }, true)
  }

  #isValidBlockBanner() {
    return Object.values(this.content).reduce((isValid, value) => {
      return Boolean(
        isValid && value.title && value.description && value.ctaText && value.url && value.image
      )
    }, true)
  }

  #isValidHeroBanner() {
    return Object.values(this.content).reduce((isValid, value) => {
      return Boolean(isValid && value.title && value.image)
    }, true)
  }

  getAnnouncementBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidAnnouncementBanner()) {
      return undefined
    }

    return {
      title: banner.title.getListOfText(this.language.value),
      bgColor: banner.bgColor?.value,
      titleColor: banner.titleColor?.value,
    }
  }

  getBillboardBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidBillboardBanner()) {
      return undefined
    }

    return {
      title: banner.title.getListOfText(this.language.value),
      description: banner.description?.getText(this.language.value),
      url: banner.url?.value,
      ctaText: banner.ctaText?.getText(this.language.value),
      bgColor: banner.bgColor?.value,
      titleColor: banner.titleColor?.value,
      descriptionColor: banner.descriptionColor?.value,
      image: banner.image?.getTranslatedImage(this.language.value),
    }
  }

  getBlockBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidBlockBanner() || !banner.image) {
      return undefined
    }

    return {
      title: banner.title.getListOfText(this.language.value),
      description: banner.description?.getText(this.language.value),
      url: banner.url?.value,
      ctaText: banner.ctaText?.getText(this.language.value),
      image: banner.image?.getTranslatedImage(this.language.value),
    }
  }

  getHeroBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidHeroBanner()) {
      return undefined
    }

    const imagesByDevice = banner.image
      ? {
          srcSet: {
            mobile: banner.image.srcSet.at(0),
            tablet: banner.image.srcSet.at(1) ?? banner.image.srcSet.at(0),
            desktop:
              banner.image.srcSet.at(2) ?? banner.image.srcSet.at(1) ?? banner.image.srcSet.at(0),
          },
          alt: banner.image.alt.getText(this.language.value),
        }
      : undefined

    return {
      title: banner.title.getListOfText(this.language.value),
      image: imagesByDevice,
    }
  }

  getType() {
    return this.type.value
  }

  getSubtype() {
    return this.subtype?.value
  }
}
